"use client";

// The system-wide 404 — the one page you arrive at by being nowhere.
//
// It is deliberately the QUIETEST surface we have: a single living ground, the
// mark, one line, one door. No organism canvas (an error page must paint
// instantly and cost nothing), no reading ramp, no sections — the netti here is
// the MARK itself, turning with its centre cell erect exactly as it does while
// the app assembles (OrganismSpinner, law: only the petals turn).
//
// The ground is this page's own blue and it DRIFTS — a ~110s arc through the
// blue family and back to where it started. The drift is a keyframe on ONE
// fixed layer's background-color, never a transition on a registered :root
// custom property (which would invalidate the whole document every frame).
//
// While the ≡ takeover owns the screen the ink ladder returns to its normal
// dark regime (`[data-menu]`) — the same move the landing makes — because the
// veil is a pale wash of this ground and white list items would vanish in it.

import { useEffect, useState } from "react";
import SiteMenu from "./SiteMenu";
import OrganismSpinner from "./brand/OrganismSpinner";
import { PillButton } from "./ui";
import type { MenuSpace } from "../lib/nav/siteNav";
import DoorMark from "./brand/DoorMark";

/** the ground this page starts on (the drift returns here every cycle). */
const NF_BLUE = "#31a1e7";

/** a 404 has no sections of its own — the ≡ opens straight at the innernet root. */
const NF_SPACE: MenuSpace = { id: "notfound", label: "innernet", items: [] };

export default function NotFound() {
  const [menuOpen, setMenuOpen] = useState(false);

  // Two things the page can only say from the document root:
  //   • the ground goes on the BODY as well, so an overscroll bounce (and the
  //     browser chrome that samples the canvas colour) never flashes the cream.
  //   • the reserved scrollbar gutter goes away. The app keeps
  //     `scrollbar-gutter: stable` so reading surfaces don't jump when a
  //     scrollbar appears — but this page never scrolls, and the gutter is a
  //     15px strip OUTSIDE the fixed layers, so the drifting ground and the
  //     menu veil both stopped short of it and left a seam down the right edge.
  useEffect(() => {
    const root = document.documentElement;
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = NF_BLUE;
    // set INLINE, not via a class: `scrollbar-gutter: auto` is the initial
    // value and the css minifier drops a rule that only restates an initial.
    const prevGutter = root.style.scrollbarGutter;
    root.style.scrollbarGutter = "auto";
    return () => {
      document.body.style.backgroundColor = prev;
      root.style.scrollbarGutter = prevGutter;
    };
  }, []);

  return (
    <main className="nf-root" data-menu={menuOpen || undefined}>
      {/* the drifting ground — one fixed layer, one keyframe */}
      <div className="nf-ground" aria-hidden />

      <SiteMenu current={NF_SPACE} onOpenChange={setMenuOpen} />

      <div className="nf-stack">
        <p className="nf-lockup" role="img" aria-label="404">
          <span className="nf-digit">4</span>
          <OrganismSpinner size="0.81em" tone="current" className="nf-mark" />
          <span className="nf-digit">4</span>
        </p>

        <div className="nf-say">
          <h1 className="nf-line">oops! we&rsquo;re yet to make this memory together.</h1>
          <PillButton
            as="a"
            href="/"
            variant="primary"
            className="btn-pill--solid nf-pill"
            icon={<DoorMark />}
          >
            go home
          </PillButton>
        </div>
      </div>
    </main>
  );
}
